// Opening a job (needs signal) and making things on the device (doesn't).
import { getJobById, getJobByNumber, isConnectionFailure, removeAreaApi, removeBuildingApi } from './api';
import { takenNames } from './area-photos';
import { msalInstance } from './msal';
import { newPhotoFields, PHOTO_TABLE } from './photo-rows';
import { bearingToDirection } from './compass';
import { forgetArea, forgetPhotoAndRow, forgetRecord, forgetRemovedArea, getRecord, moveRecord, startPhotoRemoval, listJobs, listPhotos, listRecords, saveJobSnapshot, savePhotoWithRow, saveRecord, type DeviceRecord, type Fields } from './device-db';
import { adoptEtagIfSynced } from './device-sync';
import { hasChildrenOnDevice } from './buildings';
import { AREA_PHOTO_NAME, GENERAL_PHOTO_NAME, nextPhotoName } from './photo-names';
import { preparePhoto } from './photo-resize';
import { whileAreaHeld } from './removed-areas';
import { loadAccessTags, refreshReference } from './reference';
import { syncNow } from './outbox';
import { copySurfaces, parseSurfaces, serialiseSurfaces } from '@higgins/m2q-pricing';
import { ensureVisitArea, visitOf, type PhotoPlace } from './condition-records';
import { canWriteTicks, copyTicks, tickRowsOf } from './tick-rows';

/**
 * Copy a job from CRM to this device. A job with no measure yet gets one
 * made here, with one building, so measuring can start straight away — they
 * sync like any other change.
 */
/** Bring the device's copy of a job up to date with CRM (keeps unsynced and conflicted records; a conflict is resolved on the area, conflicts.ts). */
async function fetchJob(number: string) {
  const { job, records, removedBuildingIds = [], removedAreaIds = [], removedPhotoIds = [] } = await getJobByNumber(number);
  await saveJobSnapshot({ ...job, removedPhotoIds }, records);
  // A removed area goes once nothing of it is still to sync from here (#543): one this device has
  // changed stays (its change can't be sent — the device shows it as a conflict), and one with photos
  // still to upload stays until they are on SharePoint, beside its other photos.
  for (const id of removedAreaIds) await forgetRemovedArea(id);
  // Then a building removed elsewhere goes too, unless this device has changed it (then it shows as a
  // conflict) or anything of it is still here (an area added offline, an area kept above): it stays so that
  // area still shows, and the area's send is set aside as no longer in CRM with Try again / Discard (refused.ts).
  const onDevice = removedBuildingIds.length ? await listRecords(job.id) : [];
  for (const id of removedBuildingIds) {
    const local = await getRecord(id);
    if (local?.state === 'synced' && !hasChildrenOnDevice(onDevice, id)) await forgetRecord(id);
  }
  // A photo removed on another device goes from this one, its row and its copy.
  for (const id of removedPhotoIds) {
    const local = await getRecord(id);
    if (!local || local.state === 'synced') await forgetPhotoAndRow(id);
  }
  return job;
}

/**
 * Remove an area (needs signal): CRM retires it, then this device forgets it
 * and its photos, and the job is refreshed for the measure's new totals. The
 * screen only offers it once the area and its photos have synced.
 */
export async function removeArea(area: DeviceRecord, jobNumber: string): Promise<void> {
  await removeAreaApi(area.id, area.etag ?? '');
  await forgetArea(area.id);
  await refreshJob(jobNumber);
}

/**
 * Add a building to the measure, on the device first (it syncs before any area
 * put in it: the outbox goes oldest first). Returns its id.
 */
export async function addBuilding(jobId: string, measureId: string, fields: Fields): Promise<string> {
  const id = crypto.randomUUID();
  await saveRecord({ id, table: 'building', jobId, parentId: measureId, etag: null, fields });
  return id;
}

/**
 * Remove an empty building (needs signal): CRM retires it, this device forgets
 * it, and areas whose photo folders lost the building's name take their new
 * etags. The screen only offers it once the building is empty and synced.
 */
export async function removeBuilding(building: DeviceRecord, jobNumber: string): Promise<void> {
  const { areaEtags = [] } = await removeBuildingApi(building.id, building.etag ?? '');
  await forgetRecord(building.id);
  for (const area of areaEtags) await adoptEtagIfSynced(area.id, area.etag);
  await refreshJob(jobNumber);
}

/** Move an area to another building of the measure: saved on the device, then synced like any edit. */
export const moveArea = (area: DeviceRecord, buildingId: string): Promise<void> => moveRecord(area.id, buildingId);

/**
 * A job already on the device, opened with signal: pick up changes made
 * elsewhere (another device, the desk) in the background. No signal, or a
 * failed fetch, leaves the device copy as it is.
 */
export async function refreshJob(number: string): Promise<void> {
  if (!navigator.onLine) return;
  try {
    await fetchJob(number);
    // The reference lists too: a job opened from the list on the device, or a CRM link to one already
    // here, never passes through openJob — and a list cached before tick rows has no tag ids.
    await refreshReference();
  } catch (err) {
    if (!isConnectionFailure(err)) console.error(`Could not refresh job ${number}`, err);
  }
}

/**
 * Copy a job to this device from an "Open in Measure2Quote" link in CRM
 * (docs/CRM-TABS.md). The link is only written once a measure exists, so
 * there is nothing to make here.
 */
export async function openJobById(id: string): Promise<void> {
  const { job, records, removedPhotoIds = [] } = await getJobById(id);
  await saveJobSnapshot({ ...job, removedPhotoIds }, records);
  await refreshReference();
}

/**
 * Open a job by its number. With no signal, only a job already on this device
 * opens (its copy as it is); with signal it is copied or refreshed from CRM.
 */
export async function openJob(number: string): Promise<string> {
  if (!navigator.onLine) {
    const onDevice = (await listJobs()).find((j) => j.number.toUpperCase() === number.toUpperCase());
    if (onDevice) return onDevice.id;
    throw new Error(`${number} isn't on this device yet. Opening a new job needs signal.`);
  }
  const job = await fetchJob(number);
  await refreshReference();

  const onDevice = await listRecords(job.id);
  if (!onDevice.some((r) => r.table === 'measure')) {
    const measureId = crypto.randomUUID();
    await saveRecord({
      id: measureId,
      table: 'measure',
      jobId: job.id,
      parentId: job.id,
      etag: null,
      fields: { hig_name: 'Original measure', hig_kind: 'Original', hig_sequence: 0, hig_source: 'Site', hig_status: 'Measuring' },
    });
    await saveRecord({
      id: crypto.randomUUID(),
      table: 'building',
      jobId: job.id,
      parentId: measureId,
      etag: null,
      fields: { hig_name: 'Building 1', hig_count: 1, hig_sortorder: 0 },
    });
    void syncNow();
  }
  return job.id;
}

export async function addArea(jobId: string, buildingId: string, sortOrder: number): Promise<string> {
  const id = crypto.randomUUID();
  await saveRecord({
    id,
    table: 'area',
    jobId,
    parentId: buildingId,
    etag: null,
    fields: { hig_name: `Area ${sortOrder + 1}`, hig_side: 'Internal', hig_sortorder: sortOrder, hig_scopenotes: null, hig_accessneeds: null },
  });
  return id;
}

/**
 * A copy of an area, to measure the next one like it ("Copy of Level 2
 * corridor"), at the end of the list: its surfaces (new ids), side, down time,
 * abseil and site requirements. Not its photos, notes or the PM's assessment
 * (SCHEMA.md "Before UAT" §4). Throws if its surfaces are in a newer format,
 * or if its ticks are rows and the device's tag list is too old to copy them.
 * Copied from the stored area, which has the last save; the screen's may be older.
 */
export async function duplicateArea(area: DeviceRecord, sortOrder: number): Promise<string> {
  const id = crypto.randomUUID();
  const f = ((await getRecord(area.id)) ?? area).fields;
  const records = await listRecords(area.jobId);
  // Ticks kept as rows are copied as rows; the JSON they replaced would only read stale.
  const ticksAsRows = tickRowsOf(records, 'areatag', area.id).length > 0;
  const tags = await loadAccessTags();
  if (ticksAsRows && !canWriteTicks(tags)) {
    throw new Error('The access and hazard list on this device is out of date, so the copy would lose its ticks. Open a job while you have signal, then copy again.');
  }
  const surfaces = copySurfaces(parseSurfaces(typeof f.hig_surfaces === 'string' ? f.hig_surfaces : null));
  await saveRecord({
    id,
    table: 'area',
    jobId: area.jobId,
    parentId: area.parentId,
    etag: null,
    fields: {
      hig_name: `Copy of ${String(f.hig_name ?? '')}`,
      hig_side: f.hig_side ?? null,
      hig_sortorder: sortOrder,
      hig_surfaces: serialiseSurfaces(surfaces),
      hig_measuredhours: f.hig_measuredhours ?? null,
      hig_downtimepercent: f.hig_downtimepercent ?? null,
      hig_abseilpercent: f.hig_abseilpercent ?? null,
      hig_sitereqs: ticksAsRows ? null : (f.hig_sitereqs ?? null),
      hig_accessneeds: f.hig_accessneeds ?? null,
      hig_scopenotes: null,
    },
  });
  const copy = await getRecord(id);
  if (ticksAsRows && copy) await copyTicks([...records, copy], area, copy, tags);
  return id;
}

export async function updateFields(record: DeviceRecord, changes: Fields): Promise<void> {
  await saveRecord({ ...record, fields: { ...record.fields, ...changes } });
}

/** The surface a photo is of: its id in the area's surfaces, and the name its file takes. */
interface PhotoSurface {
  id: string;
  name: string;
}

/** What a photo is of: one surface, the area's own perspective shot (#437), or the area in general (null). */
export const AREA_PHOTO = 'area-photo';
export type PhotoSubject = PhotoSurface | typeof AREA_PHOTO | null;

/**
 * Save a photo on the device and queue its upload. Named after its surface
 * ("Walls 03.jpg"), "Feature photo" for the area's own, or "General"; the name is fixed now,
 * so a re-sent upload never duplicates. Taken and library photos alike;
 * `place` (photo-place.ts) is where, which way and when it was taken.
 * `rowFields` go on its row too (a retake's evidence point).
 */
export const addPhoto = (jobId: string, areaId: string, file: File, subject: PhotoSubject, place: PhotoPlace, rowFields: Fields = {}): Promise<void> =>
  // Held throughout: a refresh never forgets an area someone removed elsewhere while its photo is half-saved (#543).
  whileAreaHeld(areaId, () => storePhoto(jobId, areaId, file, subject, place, rowFields));

async function storePhoto(jobId: string, areaId: string, file: File, subject: PhotoSubject, place: PhotoPlace, rowFields: Fields): Promise<void> {
  const areaPhoto = subject === AREA_PHOTO;
  const surface = areaPhoto ? null : subject;
  const { image, thumbnail, width, height } = await preparePhoto(file);
  const area = await getRecord(areaId);
  const measure = await measureOfArea(areaId);
  if (!area || !measure) throw new Error('This area isn’t on the device any more.');
  const records = await listRecords(jobId);
  // Names used by photos from other devices too: a repeat would replace theirs on SharePoint.
  const existing = takenNames(area, { records, onDevice: await listPhotos(areaId) });
  const id = crypto.randomUUID();
  const fileName = nextPhotoName(areaPhoto ? AREA_PHOTO_NAME : (surface?.name ?? GENERAL_PHOTO_NAME), existing);
  // A job with a condition report: the photo is evidence too, with where it was taken (one set of photos — DD).
  const visit = visitOf(records);
  const condition = visit ? { visitId: visit.id, visitAreaId: (await ensureVisitArea(visit, area)).id } : null;
  // Its row and the photo together; the row goes first, ahead of the upload that fills in the file (outbox order).
  await savePhotoWithRow({
    id,
    table: PHOTO_TABLE,
    jobId,
    parentId: measure.id,
    etag: null,
    fields: {
      ...newPhotoFields({
        fileName,
        areaId,
        surface,
        feature: areaPhoto,
        place,
        size: { width, height },
        takenBy: msalInstance.getActiveAccount()?.localAccountId ?? null,
        condition,
      }),
      ...rowFields,
    },
  }, {
    id,
    areaId,
    jobId,
    surfaceId: surface?.id ?? null,
    ...(areaPhoto ? { areaPhoto } : {}),
    fileName,
    image,
    thumbnail,
    state: 'waiting',
    takenAt: place.takenAt,
    bearing: place.bearing,
    direction: place.bearing === null ? null : bearingToDirection(place.bearing),
    position: place.position,
  });
  void syncNow();
}

/**
 * Remove a photo (works offline): it goes from every screen now; once sent,
 * CRM keeps its row as inactive and its file goes to SharePoint's recycle bin.
 */
export async function removePhoto(row: DeviceRecord): Promise<void> {
  await startPhotoRemoval(row);
  void syncNow();
}

/** The measure an area belongs to (area → building → measure), on the device. */
export async function measureOfArea(areaId: string): Promise<DeviceRecord | undefined> {
  const area = await getRecord(areaId);
  const building = area && (await getRecord(area.parentId));
  return building && getRecord(building.parentId);
}
